export type GeometryUnit = "mm" | "in";
export type ThicknessUnit = "mm" | "in" | "mil";

export type ConfigurationStatus =
  | "Draft"
  | "Configured"
  | "Review Required"
  | "CAD Required"
  | "Warning"
  | "Error"
  | "Approved"
  | "Released";

export type WarningLevel = "info" | "warning" | "error" | "approved";

export type EquipmentKind = "blast-frame" | "induction-coil";

export interface ProjectInformation {
  projectNumber: string;
  projectName: string;
  customer: string;
  engineer: string;
  projectRevision: string;
  configurationDate: string;
}

export interface PipeSpecification {
  outsideDiameter: string;
  outsideDiameterUnit: GeometryUnit;
  wallThickness: string;
  wallThicknessUnit: ThicknessUnit;
  pipeGrade: string;
  pipeLength: string;
  pipeLengthUnit: GeometryUnit;
  pipeTolerance: string;
  pipeGeometry: string;
  cutbackLongSide: string;
  cutbackLongSideUnit: GeometryUnit;
  cutbackShortSide: string;
  cutbackShortSideUnit: GeometryUnit;
  cutbackPlusTolerance: string;
  cutbackMinusTolerance: string;
  chamferAngleDeg: string;
  chamferPlusToleranceDeg: string;
  chamferMinusToleranceDeg: string;
  cwcCutbackLongSide: string;
  cwcCutbackShortSide: string;
  ppToeLength: string;
  ppToeThickness: string;
  coatingPlusTolerance: string;
  coatingMinusTolerance: string;
  overlapOnFbe: string;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ProcessSpecification {
  coatingType: string;
  coatingThickness: string;
  coatingThicknessUnit: ThicknessUnit;
  heatingRequirement: string;
  blastRequirement: string;
  surfacePreparation: string;
}

export interface ProjectSpecification {
  project: ProjectInformation;
  pipe: PipeSpecification;
  process: ProcessSpecification;
}

export interface EngineeringWarning {
  level: WarningLevel;
  title: string;
  message: string;
}

export interface CadCheck {
  id: string;
  label: string;
  confirmed: boolean;
}

export interface EquipmentFeature {
  name: string;
  required: boolean;
  selected: boolean;
}

export interface EquipmentOption {
  name: string;
  status: "Selected" | "Not Selected" | "Review Required";
}

export interface EngineeringDecision {
  question: string;
  input: string;
  rule: string;
  result: string;
  ruleVersion: string;
}

export interface CalculatedValue {
  label: string;
  value: string;
}

export interface EngineeringOverride {
  field: string;
  calculated: string;
  override: string;
  reason: string;
}

export interface EquipmentConfiguration {
  kind: EquipmentKind;
  configurationId: string;
  diameterCategory: string;
  lengthCategory?: string;
  status: ConfigurationStatus;
  ruleVersion: string;
  keyDimensions: CalculatedValue[];
  features: EquipmentFeature[];
  options: EquipmentOption[];
  calculations: CalculatedValue[];
  decisions: EngineeringDecision[];
  warnings: EngineeringWarning[];
  cadChecks: CadCheck[];
  overrides: EngineeringOverride[];
  notes: string;
}

export interface ConfigurationResult {
  configurationId: string;
  revision: string;
  status: ConfigurationStatus;
  createdAt: string;
  project: ProjectInformation;
  pipe: PipeSpecification;
  process: ProcessSpecification;
  blastFrame: EquipmentConfiguration | null;
  inductionCoil: EquipmentConfiguration | null;
  warnings: EngineeringWarning[];
  errorMessage?: string;
}

export interface BuildSpecification {
  configurationId: string;
  revision: string;
  status: ConfigurationStatus;
  project: ProjectInformation;
  equipment: EquipmentKind;
  equipmentLabel: string;
  equipmentConfigurationId: string;
  classification: CalculatedValue[];
  requiredFeatures: string[];
  selectedOptions: string[];
  engineeringParameters: CalculatedValue[];
  cadRequirements: string;
  engineeringStatus: string;
}

export interface ConfigurationService {
  listConfigurations(): Promise<ConfigurationResult[]>;
  createConfiguration(project: ProjectSpecification): Promise<ConfigurationResult>;
  getConfiguration(configurationId: string): Promise<ConfigurationResult>;
  getBuildSpecification(
    configurationId: string,
    equipment: EquipmentKind,
  ): Promise<BuildSpecification>;
  updateCadChecks(
    configurationId: string,
    equipment: EquipmentKind,
    checks: CadCheck[],
  ): Promise<ConfigurationResult>;
  applyOverride(
    configurationId: string,
    equipment: EquipmentKind,
    override: EngineeringOverride,
  ): Promise<ConfigurationResult>;
}

export const emptyProjectInformation = (): ProjectInformation => ({
  projectNumber: "",
  projectName: "",
  customer: "",
  engineer: "",
  projectRevision: "A",
  configurationDate: new Date().toISOString().slice(0, 10),
});

export const emptyPipeSpecification = (): PipeSpecification => ({
  outsideDiameter: "",
  outsideDiameterUnit: "mm",
  wallThickness: "",
  wallThicknessUnit: "mm",
  pipeGrade: "",
  pipeLength: "",
  pipeLengthUnit: "mm",
  pipeTolerance: "",
  pipeGeometry: "Straight",
  cutbackLongSide: "",
  cutbackLongSideUnit: "mm",
  cutbackShortSide: "",
  cutbackShortSideUnit: "mm",
  cutbackPlusTolerance: "",
  cutbackMinusTolerance: "",
  chamferAngleDeg: "",
  chamferPlusToleranceDeg: "",
  chamferMinusToleranceDeg: "",
  cwcCutbackLongSide: "",
  cwcCutbackShortSide: "",
  ppToeLength: "",
  ppToeThickness: "",
  coatingPlusTolerance: "",
  coatingMinusTolerance: "",
  overlapOnFbe: "",
});

export const emptyProcessSpecification = (): ProcessSpecification => ({
  coatingType: "",
  coatingThickness: "",
  coatingThicknessUnit: "mm",
  heatingRequirement: "",
  blastRequirement: "",
  surfacePreparation: "",
});
